import React, { useEffect, useMemo, useState } from 'react';

import { Web } from '@mui/icons-material';
import dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import { useLocation, useNavigate } from 'react-router-dom';

import { generateReport, getReportData, removeReportData, getList } from '../operations';
import ClientContactActivityForm from './ClientContactActivityForm';
import ClientContactActivityTable from './ClientContactActivityTable';
import { getCsvData } from './utils.js';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, SubHeader, EdgeReportingWindow, ReportLoader } from '../../../components';
import ErrorModal from '../../../components/ErrorModal';
import { formatIsoToDate, newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger.js';

import './styles.scoped.css';

const utc = require('dayjs/plugin/utc');

const ClientContactActivity = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [form, setForm] = useState({});
  const [activityTypes, setActivityTypes] = useState([]);
  const [ownerCodes, setOwnerCodes] = useState([]);
  const [lineOfBusinesses, setLineOfBusinesses] = useState([]);
  const [isLoadingOwnerCodes, setIsLoadingOwnerCodes] = useState(false);
  const [isLoadingActivityTypes, setIsLoadingActivityTypes] = useState(false);
  const [isLoadingLineOfBusinesses, setIsLoadingLineOfBusinesses] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState();
  const [showError, setShowError] = useState(false);

  dayjs.extend(utc);

  const getOwnerCodesToSelect = async () => {
    try {
      const newOwnerCodes = await getList(`/pccodes`);
      setOwnerCodes(newOwnerCodes);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const getActivityTypeToSelect = async () => {
    try {
      const newActivityTypes = await getList(`/calltypes`);
      setActivityTypes(newActivityTypes);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const getLineOfBusinessToSelect = async () => {
    try {
      const newLineofBusinesses = await getList(`/line-of-business`);
      setLineOfBusinesses(newLineofBusinesses);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const handleGetData = async () => {
    // Owner Codes
    setIsLoadingOwnerCodes(true);
    getOwnerCodesToSelect()
      .catch(error => console.log('error', error))
      .finally(() => {
        setIsLoadingOwnerCodes(false);
      });

    // Activity Types
    setIsLoadingActivityTypes(true);
    getActivityTypeToSelect()
      .catch(error => console.log('error', error))
      .finally(() => setIsLoadingActivityTypes(false));

    // Line Of Business
    setIsLoadingLineOfBusinesses(true);
    getLineOfBusinessToSelect()
      .catch(error => console.log('error', error))
      .finally(() => setIsLoadingLineOfBusinesses(false));
  };

  useEffect(() => {
    handleGetData();
  }, []);

  const activityTypeOptions = useMemo(
    () =>
      activityTypes.map(({ REF_CODE, REF_DESCRIPTION }) => ({
        label: REF_DESCRIPTION,
        value: REF_CODE,
      })),
    [activityTypes],
  );

  const lobOptions = useMemo(
    () =>
      lineOfBusinesses.map(({ LineOfBusinessCode, LineOfBusinessDesc }) => ({
        label: LineOfBusinessDesc,
        value: LineOfBusinessCode,
      })),
    [lineOfBusinesses],
  );

  const ownerCodeOptions = useMemo(
    () =>
      ownerCodes.map(({ REF_CODE, REF_DESCRIPTION }) => ({
        label: REF_DESCRIPTION,
        value: REF_CODE,
      })),
    [ownerCodes],
  );

  const loginOwnerCodeOptions = useMemo(
    () =>
      ownerCodes.map(({ REF_CODE, REF_DESCRIPTION }) => ({
        label: REF_DESCRIPTION,
        value: REF_CODE,
      })),
    [ownerCodes],
  );

  const navigateCancelAction = () => {
    navigate(`/edge-reporting`);
  };

  const fileName = `edge-reporting-client-contact-activity-${newDateFormatted.toString()}.csv`;

  const handleViewResults = () => {
    if (
      !(
        dayjs(form?.ActivityTodate).isAfter(form?.ActivityFromdate) ||
        dayjs(form?.ActivityFromdate).isSame(form?.ActivityFromdate)
      )
    ) {
      alert('Invalid date range. Please double check and try again.');
      return;
    }
    setLoadingResults(true);
    Promise.all([
      generateReport({
        endpoint: 'client-contact-activity',
        payload: {
          ActivityFromdate: form?.ActivityFromdate ? formatIsoToDate(form?.ActivityFromdate, 'YYYY-MM-DD') : '',
          ActivityTodate: form?.ActivityTodate ? formatIsoToDate(form?.ActivityTodate, 'YYYY-MM-DD') : '',
          LastContactFromdate: form?.LastContactFromdate
            ? formatIsoToDate(form?.LastContactFromdate, 'YYYY-MM-DD')
            : '',
          LastContactTodate: form?.LastContactTodate ? formatIsoToDate(form?.LastContactTodate, 'YYYY-MM-DD') : '',
          FollowupFromdate: form?.FollowupFromdate ? formatIsoToDate(form?.FollowupFromdate, 'YYYY-MM-DD') : '',
          FollowupTodate: form?.FollowupTodate ? formatIsoToDate(form?.FollowupTodate, 'YYYY-MM-DD') : '',
          OwnerCodes: form?.OwnerCodes ?? '',
          LoginOwnerCode: form?.LoginOwnerCode ?? '',
          ActivityType: form?.ActivityType ?? '',
          FromClientName: form?.FromClientName ?? '',
          ToClientName: form?.ToClientName ?? '',
          FromClientID: form?.FromClientID ?? '',
          ToClientID: form?.ToClientID ?? '',
          FromClientBillToID: form?.FromClientBillToID ?? '',
          ToClientBillToID: form?.ToClientBillToID ?? '',
          LOB: form?.LOB ?? '',
        },
      }),
    ])
      .then(values => {
        values.forEach((response, index) => {
          if (response.location) {
            getReportData(response.location).then(async reportData => {
              if (reportData?.length) {
                await removeReportData(response.location);
                if (index === 0) {
                  setData(reportData);
                }
                setShowResults(true);
              }
            });
          } else if (response.statusCode !== 404) {
            if (index === 0) {
              setData(response);
            }
            setShowResults(true);
          } else {
            // eslint-disable-next-line no-undef
            alert('No data found matching your criteria.');
          }
        });
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setLoadingResults(false);
      });
  };

  useEffect(() => {
    setErrors([]);
  }, [location]);

  return (
    <>
      <SubHeader
        text="Edge Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Client Contact Activity?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Client Contact Activity'}
          midItemAction={() => {
            setShowResults(false);
          }}
          rightTitle={showResults ? 'Viewing Report' : 'Client Contact Activity'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <CSVLink filename={fileName} data={getCsvData(data)}>
            <ImageColorChanger
              src={Excel}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />
          </CSVLink>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showError && <ErrorModal dismiss={() => setShowError(false)} error="No data found for report." />}
      {showResults ? (
        <>
          <ClientContactActivityTable data={!loadingResults ? data : []} loading={loadingResults} to="" />
          <div className="buttons-row">
            <Button
              variant="primary"
              className=""
              onPress={() => setShowResults(false)}
              style={{ marginRight: '20px', width: '100px' }}
              text="Back"
            />

            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <ClientContactActivityForm
              activityTypeOptions={activityTypeOptions}
              lobOptions={lobOptions}
              ownerCodeOptions={ownerCodeOptions}
              loginOwnerCodeOptions={loginOwnerCodeOptions}
              isLoadingOwnerCodes={isLoadingOwnerCodes}
              isLoadingActivityTypes={isLoadingActivityTypes}
              isLoadingLineOfBusinesses={isLoadingLineOfBusinesses}
              errors={errors}
              setErrors={setErrors}
              navigateCancelAction={navigateCancelAction}
              setForm={setForm}
              setLoadingResults={setLoadingResults}
              setShowResults={setShowResults}
              form={form}
              handleViewResults={handleViewResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default ClientContactActivity;
